import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listRadiologyCentres,
  listRadiologyStock,
  downloadRadiologyTemplate,
  bulkUploadRadiology,
  updateRadiologyStockRow,
  deleteRadiologyStockRow,
  setImagingStudyImage,
} from '../../api/radiologyStock.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import CatalogueImageThumb from '../../components/CatalogueImageThumb.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { useDebounce } from '../../hooks/useDebounce.js'
import { useAuth } from '../../auth/AuthProvider.jsx'

/**
 * Radiology centre catalogue self-service. Radiology staff manage the
 * studies their centre offers — adding, pricing, setting turnaround
 * time, toggling availability. Radiology staff are auto-scoped to
 * their own centre via the RadiologyStaffLink; platform admins pick
 * the target centre from a dropdown.
 */
export default function RadiologyCatalogue() {
  const { user } = useAuth()
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const linkedCentreId = user?.radiology_profile?.id ?? null
  const linkedCentreName = user?.radiology_profile?.name ?? ''

  const centresQ = useQuery({
    queryKey: ['radiology-centres-list'],
    queryFn: listRadiologyCentres,
    enabled: isAdmin,
  })
  const [centreId, setCentreId] = useState(linkedCentreId)
  if (isAdmin && !centreId && centresQ.data?.[0]?.id) {
    setCentreId(centresQ.data[0].id)
  }

  if (!isAdmin && !linkedCentreId) {
    return (
      <Card>
        <CardHeader
          title="My catalogue"
          subtitle="Catalogue self-service for radiology staff"
        />
        <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
          <b>Account not linked to a radiology centre yet.</b>
          <p className="mt-1">
            Ask the admin to bind your user
            (<span className="font-mono">{user?.username}</span>) via Django
            admin → Radiology → Radiology staff links → Add.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="My catalogue"
          subtitle="Manage the imaging studies your centre offers: download the template, fill in price + turnaround, upload it back. Edit individual rows below."
        />
        {isAdmin ? (
          <div className="mt-2">
            <label className="text-xs uppercase tracking-wide text-slate-500">
              Radiology centre
              <span className="ml-2 text-amber-700 normal-case font-normal">(admin — pick any)</span>
            </label>
            <select
              value={centreId || ''}
              onChange={(e) => setCentreId(Number(e.target.value))}
              className="mt-1 w-full border rounded px-3 py-2 text-sm bg-white"
            >
              {(centresQ.data || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="mt-2 text-sm">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-2">Centre</span>
            <span className="font-semibold text-slate-800">{linkedCentreName}</span>
            {user?.radiology_profile?.is_radiology_admin && (
              <span className="ml-2 text-[10px] uppercase border rounded-full px-2 py-0.5 bg-wantok-50 text-wantok-700 border-wantok-100">
                centre admin
              </span>
            )}
          </div>
        )}
      </Card>

      {centreId && (
        <>
          <TemplateCard />
          <UploadCard centreId={centreId} />
          <StockListCard centreId={centreId} />
        </>
      )}
    </div>
  )
}

function TemplateCard() {
  const MODALITIES = [
    ['', 'All modalities'],
    ['XR', 'X-ray (XR)'],
    ['US', 'Ultrasound (US)'],
    ['CT', 'CT'],
    ['MRI', 'MRI'],
    ['DEXA', 'DEXA'],
    ['MAMMO', 'Mammography'],
    ['FLUORO', 'Fluoroscopy'],
    ['ANGIO', 'Angiography'],
    ['NM', 'Nuclear medicine'],
    ['PET', 'PET'],
    ['ECHO', 'Echocardiography'],
  ]
  const [modality, setModality] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(null)

  async function handleDownload() {
    setError(null); setDownloading(true)
    try {
      const blob = await downloadRadiologyTemplate({ modality })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `wantok_imaging_template${modality ? '_' + modality.toLowerCase() : ''}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) { setError(e) }
    finally { setDownloading(false) }
  }

  return (
    <Card>
      <CardHeader title="1. Download template"
        subtitle="A CSV with every imaging study in the global catalogue. Fill in price, turnaround_time, is_offered on the rows your centre actually offers; leave the rest blank." />
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-slate-500">Modality:</span>
          <select
            value={modality}
            onChange={(e) => setModality(e.target.value)}
            className="border rounded px-2 py-1 text-sm bg-white"
          >
            {MODALITIES.map(([v, l]) => <option key={v || 'all'} value={v}>{l}</option>)}
          </select>
        </label>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60"
        >
          {downloading ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>
      {error && <div className="mt-3"><ErrorBanner error={error} /></div>}
    </Card>
  )
}

function UploadCard({ centreId }) {
  const qc = useQueryClient()
  const fileRef = useRef(null)
  const [file, setFile] = useState(null)
  const [result, setResult] = useState(null)

  const mut = useMutation({
    mutationFn: (f) => bulkUploadRadiology({ file: f, centreId }),
    onSuccess: (data) => {
      setResult(data)
      qc.invalidateQueries({ queryKey: ['radiology-stock', centreId] })
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
    },
  })

  return (
    <Card>
      <CardHeader title="2. Upload your filled-in CSV"
        subtitle="Multipart upload — only rows where you filled in price, turnaround_time, notes, or set is_offered=true are processed. Re-uploading is safe." />
      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
        <button onClick={() => file && mut.mutate(file)} disabled={!file || mut.isPending}
          className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60">
          {mut.isPending ? 'Uploading…' : 'Upload'}
        </button>
        {file && <span className="text-xs text-slate-500">{file.name} · {Math.round(file.size / 1024)} KB</span>}
      </div>
      {mut.error && <div className="mt-3"><ErrorBanner error={mut.error} /></div>}
      {result && <UploadResult data={result} />}
    </Card>
  )
}

function UploadResult({ data }) {
  const s = data?.summary || {}
  const errors = data?.errors || []
  const proposals = data?.proposals || []
  const Stat = ({ label, value, colour = 'slate' }) => {
    const map = {
      slate: 'border-slate-200 bg-slate-50 text-slate-700',
      emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
      sky: 'border-sky-200 bg-sky-50 text-sky-800',
      amber: 'border-amber-200 bg-amber-50 text-amber-800',
      rose: 'border-rose-200 bg-rose-50 text-rose-800',
    }
    return (
      <div className={`border rounded px-2 py-1.5 text-center ${map[colour]}`}>
        <div className="text-lg font-semibold">{value ?? 0}</div>
        <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
      </div>
    )
  }
  return (
    <div className="mt-4 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="Rows processed" value={s.rows_processed} />
        <Stat label="Created" value={s.stock_created} colour="emerald" />
        <Stat label="Updated" value={s.stock_updated} colour="sky" />
        <Stat label="Proposals" value={s.proposals_created} colour="amber" />
        <Stat label="Errors" value={s.errors} colour={s.errors ? 'rose' : 'slate'} />
      </div>
      {errors.length > 0 && (
        <div className="border border-rose-200 bg-rose-50 rounded p-2">
          <div className="text-[11px] uppercase tracking-wide text-rose-700 mb-1">Row errors ({errors.length})</div>
          <ul className="text-xs text-rose-800 space-y-0.5 max-h-40 overflow-auto">
            {errors.map((e, i) => <li key={i}><span className="font-mono">row {e.row}</span> · {e.error}</li>)}
          </ul>
        </div>
      )}
      {proposals.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded p-2">
          <div className="text-[11px] uppercase tracking-wide text-amber-700 mb-1">
            Filed as study proposals ({proposals.length}) — admin will review
          </div>
          <ul className="text-xs text-amber-900 space-y-0.5 max-h-40 overflow-auto">
            {proposals.map((p) => (
              <li key={p.proposal_id}>
                <span className="font-mono">row {p.row}</span> · <b>{p.exam_name}</b>
                {p.modality && <> [{p.modality}]</>}
                <span className="text-amber-600"> #{p.proposal_id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function StockListCard({ centreId }) {
  const { user } = useAuth()
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search, 250)
  const stockQ = useQuery({
    queryKey: ['radiology-stock', centreId, debounced],
    queryFn: () => listRadiologyStock({ centreId, search: debounced }),
    enabled: Boolean(centreId),
  })
  const rows = useMemo(() => stockQ.data || [], [stockQ.data])
  const filtering = Boolean(debounced?.trim())
  const subtitle = filtering
    ? `Showing ${rows.length} match${rows.length === 1 ? '' : 'es'} for "${debounced}". Clear the filter to see your full catalogue.`
    : `${rows.length} stud${rows.length === 1 ? 'y' : 'ies'} in your catalogue. Edit price, turnaround, or availability inline. Deleting a row means you no longer offer that study.`

  return (
    <Card>
      <CardHeader title="3. Current catalogue" subtitle={subtitle}
        right={<button onClick={() => stockQ.refetch()} className="text-xs text-wantok-700 underline">Refresh</button>}
      />
      <input value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter by exam name, modality, or body region…"
        className="w-full border rounded px-3 py-2 text-sm mb-3" />
      {stockQ.error && <ErrorBanner error={stockQ.error} onRetry={() => stockQ.refetch()} />}
      {stockQ.isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400 italic">No studies yet. Upload a CSV above to populate.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
              <tr>
                <th className="p-2 w-12"></th>
                <th className="text-left p-2">Study</th>
                <th className="text-left p-2">Modality</th>
                <th className="text-right p-2">Price (PGK)</th>
                <th className="text-left p-2">TAT</th>
                <th className="text-center p-2">Offered</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <StockRow key={r.id} row={r}
                  isAdmin={isAdmin}
                  onChanged={() => qc.invalidateQueries({ queryKey: ['radiology-stock', centreId] })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function StockRow({ row, isAdmin, onChanged }) {
  const [price, setPrice] = useState(row.price ?? '')
  const [tat, setTat] = useState(row.turnaround_time || '')
  const [offered, setOffered] = useState(row.is_offered)

  // Re-sync local state when the server-side row updates (e.g. after a
  // CSV bulk upload invalidates the catalogue query). Otherwise useState
  // only reads row.* on mount and the inputs stay blank even though the
  // refetched row has the new values.
  useEffect(() => {
    setPrice(row.price ?? '')
    setTat(row.turnaround_time || '')
    setOffered(row.is_offered)
  }, [row.id, row.last_updated])

  const dirty = (
    String(price) !== String(row.price ?? '') ||
    tat !== (row.turnaround_time || '') ||
    offered !== row.is_offered
  )

  const save = useMutation({
    mutationFn: () => updateRadiologyStockRow(row.id, {
      price: price === '' ? null : price,
      turnaround_time: tat,
      is_offered: Boolean(offered),
    }),
    onSuccess: onChanged,
  })
  const del = useMutation({
    mutationFn: () => deleteRadiologyStockRow(row.id),
    onSuccess: onChanged,
  })

  return (
    <tr className={dirty ? 'bg-amber-50/40' : ''}>
      <td className="p-2">
        <CatalogueImageThumb
          url={row.study_image_url}
          alt={row.exam_name}
          editable={isAdmin}
          onUpload={async (file) => {
            await setImagingStudyImage(row.imaging_study, file)
            onChanged()
          }}
          onClear={async () => {
            await setImagingStudyImage(row.imaging_study, null)
            onChanged()
          }}
        />
      </td>
      <td className="p-2">
        <div className="font-medium">
          {row.exam_name}
          {row.short_code && <span className="ml-2 text-[10px] uppercase text-slate-500 font-mono">{row.short_code}</span>}
        </div>
        <div className="text-[11px] text-slate-500">
          {row.body_region}
          {row.contrast && <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-amber-50 text-amber-800 border-amber-200">contrast</span>}
          {row.requires_appointment && <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-slate-50 text-slate-600 border-slate-200">appt</span>}
        </div>
      </td>
      <td className="p-2 text-xs text-slate-700 font-medium">{row.modality}</td>
      <td className="p-2 text-right">
        <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
          className="w-24 border rounded px-2 py-1 text-xs text-right" />
      </td>
      <td className="p-2">
        <input value={tat} onChange={(e) => setTat(e.target.value)}
          placeholder="e.g. Same day"
          className="w-32 border rounded px-2 py-1 text-xs" />
      </td>
      <td className="p-2 text-center">
        <input type="checkbox" checked={Boolean(offered)} onChange={(e) => setOffered(e.target.checked)} />
      </td>
      <td className="p-2 text-right whitespace-nowrap">
        <button onClick={() => save.mutate()} disabled={!dirty || save.isPending}
          className="text-xs bg-wantok-700 hover:bg-wantok-800 text-white rounded px-2 py-1 disabled:opacity-40">
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button onClick={() => { if (window.confirm(`Stop offering ${row.exam_name}?`)) del.mutate() }}
          disabled={del.isPending}
          className="ml-2 text-xs text-rose-600 hover:underline disabled:opacity-40">
          Delete
        </button>
      </td>
    </tr>
  )
}
